import React, { useState, useEffect, useRef } from "react";
import { FaFacebookF, FaInstagram, FaLinkedin, FaPhoneAlt, FaTwitter } from "react-icons/fa";
import { MdEmail, MdLocationOn } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import logoImg from "../pictures/Logo2.PNG";
import "../styles/Navbar.css";
import { Link } from "react-router-dom";
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";

// Find this function in Navbar.jsx
const slugify = (text) => {
  if (!text) return ""; // <--- Add this safety check
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [hideHeader, setHideHeader] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesRes = await fetch("https://backend.afrivisionconsultants.com/public/services");
        // const servicesRes = await fetch("http://localhost:5000/public/services");
        const servicesData = await servicesRes.json();
        // Safe check: Only set if it's an array, otherwise default to empty array
        setServiceGroups(Array.isArray(servicesData) ? servicesData : []);
        
        const blogRes = await fetch("https://backend.afrivisionconsultants.com/public/blog/categories");
        // const blogRes = await fetch("http://localhost:5000/public/blog/categories");
        const blogData = await blogRes.json();
        // Safe check for blog categories as well
        setBlogCategories(Array.isArray(blogData) ? blogData : []);
      } catch (err) {
        console.error("Failed to fetch navbar data:", err);
        setServiceGroups([]);
        setBlogCategories([]);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth <= 768;
      setIsMobile(mobileView);
      if (!mobileView) { setMenuOpen(false); setActiveDropdown(null); }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false); setActiveDropdown(null);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (currentScroll > lastScrollY && currentScroll > 120) setHideHeader(true);
      else setHideHeader(false);
      lastScrollY = currentScroll;
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDropdown = (index) => setActiveDropdown(activeDropdown === index ? null : index);
  const handleNavClick = () => { setMenuOpen(false); setActiveDropdown(null); };
  const scrollToTop = () => { scroll.scrollToTop(); handleNavClick(); };

  return (
    <header className={`header fixed ${hideHeader ? "hide" : ""}`} ref={navRef}>
      <div className="header-top">
        <div className="welcome-text">Welcome to Afrivision Consultants Ltd</div>
        <div className="social-icons">
          <a href="https://web.facebook.com/"><FaFacebookF /></a>
          <a href="https://www.instagram.com/"><FaInstagram /></a>
          <a href="https://x.com/home"><FaTwitter /></a>
          <a href="https://x.com/home"><FaLinkedin /></a>
        </div>
      </div>

      <div className="header-main">
        <div className="logo" onClick={scrollToTop}>
          <img src={logoImg} alt="Afrivision Logo" />
        </div>
        <div className="contact-info">
          <a href="tel:+250795459039"><FaPhoneAlt /> Call us on (250) 795-459-039</a>
          <a href="mailto:info@afrivisionconsultants.com"><MdEmail /> info@afrivisionconsultants.com</a>
          <a href="https://www.google.com/maps/search/?api=1&query=Kayonza,+Mukarange Sector,+Rwanda" target="_blank" rel="noopener noreferrer">
            <MdLocationOn /> Kayonza, Rwanda
          </a>
        </div>
        <div className="contact-icons">
          <a href="tel:+250795459039"><FaPhoneAlt /></a>
          <a href="mailto:info@afrivisionconsultants.com"><MdEmail /></a>
          <a href="https://www.google.com/maps/search/?api=1&query=Kayonza,+Mukarange Sector,+Rwanda" target="_blank" rel="noopener noreferrer">
            <MdLocationOn />
          </a>
        </div>
        <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">☰</button>
      </div>

      <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
        <ScrollLink to="home" smooth={true} duration={50} onClick={handleNavClick}>Home</ScrollLink>

        {/* Our Services Dropdown */}
        <div className={`dropdown ${activeDropdown === 1 ? "open" : ""}`}
            onMouseEnter={() => !isMobile && setActiveDropdown(1)}
            onMouseLeave={() => !isMobile && setActiveDropdown(null)}>
          <button className={`dropbtn ${activeDropdown === 1 ? "active" : ""}`} onClick={() => toggleDropdown(1)}>
            Our Services <IoIosArrowDown className={`arrow ${activeDropdown === 1 ? "rotate" : ""}`} />
          </button>
          <div className="dropdown-content">
            {/* Optional: A link to scroll to the top of the entire services section */}
            <ScrollLink to="our-services" smooth={true} duration={50} offset={-80} onClick={handleNavClick}>
              <strong>All Services</strong>
            </ScrollLink>
            
            {/* Dynamic Links to Specific Service Groups */}
            {serviceGroups.map((group) => (
              <ScrollLink 
                key={group.id} 
                to={slugify(group.name || group.title)} // Ensure this targets the exact slug
                smooth={true} 
                duration={50} 
                offset={-80} // Adjust this negative offset based on your Navbar's height so it doesn't cover the title
                onClick={handleNavClick}
              >
                {group.name || group.title}
              </ScrollLink>
            ))}
          </div>
        </div>

        {/* Blog Dropdown - Fixed Links */}
        <div className={`dropdown ${activeDropdown === 2 ? "open" : ""}`}
             onMouseEnter={() => !isMobile && setActiveDropdown(2)}
             onMouseLeave={() => !isMobile && setActiveDropdown(null)}>
          <button className={`dropbtn ${activeDropdown === 2 ? "active" : ""}`} onClick={() => toggleDropdown(2)}>
            Blog/News <IoIosArrowDown className={`arrow ${activeDropdown === 2 ? "rotate" : ""}`} />
          </button>
          <div className="dropdown-content">
            <Link to="/blog?category=All" onClick={handleNavClick}>All News</Link>
            {/* Added optional chaining ? */}
            {blogCategories?.map((cat) => (
                <Link key={cat.id} to={`/blog?category=${cat.slug}`} onClick={handleNavClick}>
                  {cat.name}
                </Link>
            ))}
          </div>
        </div>

        {/* Career Dropdown */}
        <div className={`dropdown ${activeDropdown === 3 ? "open" : ""}`}
             onMouseEnter={() => !isMobile && setActiveDropdown(3)}
             onMouseLeave={() => !isMobile && setActiveDropdown(null)}>
          <button className={`dropbtn ${activeDropdown === 3 ? "active" : ""}`} onClick={() => toggleDropdown(3)}>
            Career/Partnership <IoIosArrowDown className={`arrow ${activeDropdown === 3 ? "rotate" : ""}`} />
          </button>
          <div className="dropdown-content">
            <ScrollLink to="careers" smooth={true} duration={50} onClick={handleNavClick}>Career</ScrollLink>
            <ScrollLink to="partnership" smooth={true} duration={50} onClick={handleNavClick}>Partnership</ScrollLink>
          </div>
        </div>

        <ScrollLink to="about" smooth={true} duration={50} onClick={handleNavClick}>About Us</ScrollLink>
        <ScrollLink to="contact" smooth={true} duration={50} onClick={handleNavClick}>Contact Us</ScrollLink>

        {/* Mobile Info */}
        <div className="mobile-contact-info">
          <a href="tel:+250795459039"><FaPhoneAlt /> Call us on (250) 795-459-039</a>
          <a href="mailto:info@afrivisionconsultants.com"><MdEmail /> info@afrivisionconsultants.com</a>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;